import React, { useMemo, useState } from 'react';
import { workOrderLabel } from '../../utils/displayId';
import {
  WrenchScrewdriverIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Card, CardHeader, CardBody } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';

// ─── Job Costing Tab (§3.4) ──────────────────────────────────────────────────
// Materials rollup merges identical material across ALL line items and WOs
// into one row. Labor totals across WOs with an accordion to correct a single
// WO's hours. "Two doors, one data": actuals here edit the SAME record as on
// the WO (never fork).

/**
 * Roll up materials across all line items and work orders, merging by a stable
 * key (materialId || normalized name). Bid qty vs actual used (§3.1).
 */
export function rollupMaterials(lineItems = [], workOrders = []) {
  const rows = new Map();
  const add = (m, source) => {
    const key = m.materialId || m.material_id || (m.name || m.description || '').trim().toLowerCase();
    if (!key) return;
    if (!rows.has(key)) {
      rows.set(key, {
        key,
        name: m.name || m.description || 'Material',
        unit: m.unit || '',
        bidQty: 0,
        actualUsed: 0,
        sources: [],
      });
    }
    const row = rows.get(key);
    row.bidQty += Number(m.quantity ?? m.bidQty ?? 0);
    if (m.actualUsed != null || m.actual_used != null) {
      row.actualUsed += Number(m.actualUsed ?? m.actual_used ?? 0);
    }
    row.sources.push(source);
  };
  lineItems.forEach((li) => (li.materials || []).forEach((m) => add(m, `Line: ${li.description || li.item_name || ''}`)));
  workOrders.forEach((wo) => (wo.materials || []).forEach((m) => add(m, workOrderLabel(wo)))); // IDI-411
  return Array.from(rows.values());
}

export default function JobCostingTab({ job, workOrders = [], onUpdateMaterialActual, onUpdateWOLabor }) {
  const materialRows = useMemo(
    () => rollupMaterials(job?.line_items || job?.lineItems || [], workOrders),
    [job, workOrders]
  );
  const [expandedWO, setExpandedWO] = useState(null);
  const [drafts, setDrafts] = useState({}); // key -> actualUsed draft
  const [laborDrafts, setLaborDrafts] = useState({}); // woId -> hours draft

  const totalLaborHours = workOrders.reduce(
    (s, wo) => s + Number(wo.actualHours ?? wo.actual_hours ?? 0), 0
  );

  const saveMaterial = (row) => {
    const val = drafts[row.key];
    if (val == null) return;
    onUpdateMaterialActual && onUpdateMaterialActual(row, Number(val));
  };

  const saveLabor = (wo) => {
    const val = laborDrafts[wo.id];
    if (val == null) return;
    // Two doors, one data (§3.3): editing here updates the WO's record + Payroll
    onUpdateWOLabor && onUpdateWOLabor(wo, Number(val));
  };

  return (
    <div className="space-y-6">
      {/* Materials rollup (§3.1) */}
      <Card>
        <CardHeader>
          <span className="flex items-center gap-2 text-sm font-medium">
            <WrenchScrewdriverIcon className="h-4 w-4 text-gray-500" />
            Materials — Bid vs Actual
          </span>
        </CardHeader>
        <CardBody>
          {materialRows.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No materials on this job</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500">
                    <th className="py-2 pr-4">Material</th>
                    <th className="py-2 pr-4 text-right">Bid Qty</th>
                    <th className="py-2 pr-4 text-right">Actual Used</th>
                    <th className="py-2 pr-4 text-right">Variance</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {materialRows.map((row) => {
                    const actual = drafts[row.key] != null ? Number(drafts[row.key]) : row.actualUsed;
                    const variance = actual - row.bidQty;
                    return (
                      <tr key={row.key} className="border-t border-gray-100">
                        <td className="py-2 pr-4 text-gray-900">
                          {row.name}
                          <span className="block text-xs text-gray-400">
                            {row.sources.length} source{row.sources.length !== 1 ? 's' : ''}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-right text-gray-700">
                          {row.bidQty} {row.unit}
                        </td>
                        <td className="py-2 pr-4 text-right">
                          {/* IDI-315 §4 (2026-09-12): "the spacing on that was
                              wrong and needs to be moved over" — Input.js
                              wraps <input> in a <div className={className}>
                              (a BLOCK box); `text-right` on this <td> only
                              aligns INLINE content, so the block-level
                              wrapper div stayed left-edge-anchored inside a
                              right-aligned column while the "Actual Used"
                              header sat at the right edge. Not a vertical
                              label/field relationship problem (the field was
                              already structurally under its header — this is
                              a table column) — `ml-auto` pushes the block box
                              itself to the right edge to match. */}
                          <Input
                            type="number"
                            className="w-24 text-right ml-auto"
                            value={drafts[row.key] ?? row.actualUsed ?? ''}
                            onChange={(e) => setDrafts((d) => ({ ...d, [row.key]: e.target.value }))}
                          />
                        </td>
                        <td className={`py-2 pr-4 text-right ${variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-700' : 'text-gray-500'}`}>
                          {variance > 0 ? '+' : ''}{variance} {row.unit}
                        </td>
                        <td className="py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => saveMaterial(row)}>Save</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Labor (§3.2) */}
      <Card>
        <CardHeader>
          <span className="flex items-center gap-2 text-sm font-medium">
            <ClockIcon className="h-4 w-4 text-gray-500" />
            Labor — {totalLaborHours} hrs total
          </span>
        </CardHeader>
        <CardBody>
          {workOrders.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No work orders on this job</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {workOrders.map((wo) => {
                const isOpen = expandedWO === wo.id;
                const hours = wo.actualHours ?? wo.actual_hours ?? 0;
                return (
                  <div key={wo.id}>
                    <button
                      type="button"
                      onClick={() => setExpandedWO(isOpen ? null : wo.id)}
                      className="w-full flex items-center justify-between py-2.5 text-left"
                    >
                      <span className="flex items-center gap-2 text-sm text-gray-900">
                        {isOpen ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
                        {workOrderLabel(wo)}
                      </span>
                      <span className="text-sm text-gray-600">{hours} hrs</span>
                    </button>
                    {isOpen && (
                      <div className="pl-6 pb-3">
                        <p className="text-xs text-gray-500 mb-2">
                          Correct logged hours (e.g. forgot to clock in). Saving updates this work
                          order's record and the Payroll report.
                        </p>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.25"
                            className="w-28"
                            value={laborDrafts[wo.id] ?? hours}
                            onChange={(e) => setLaborDrafts((d) => ({ ...d, [wo.id]: e.target.value }))}
                          />
                          <span className="text-sm text-gray-500">hrs</span>
                          <Button size="sm" onClick={() => saveLabor(wo)}>Save</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
